/**
 * ProductReviews — storefront-facing reviews widget for a product.
 *
 * Shows aggregate rating + individual reviews and a submit form (buyers only).
 *
 * Usage:
 *   <ProductReviews subdomain="acme-store" productId="prod_123" buyerEmail={email} />
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function Stars({ rating, size = 14, onSelect }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((v) => (
        <button
          type="button"
          key={v}
          onClick={() => onSelect?.(v)}
          className={`transition-transform ${onSelect ? 'hover:scale-110 cursor-pointer' : 'cursor-default'}`}
          data-testid={`review-star-${v}`}
        >
          <Star
            size={size}
            className={v <= rating ? 'text-amber-400 fill-amber-400' : 'text-white/25'}
          />
        </button>
      ))}
    </div>
  );
}

export default function ProductReviews({ subdomain, productId, buyerEmail = '' }) {
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [average, setAverage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ rating: 5, title: '', body: '', author_name: '', buyer_email: buyerEmail });

  const load = async () => {
    if (!subdomain || !productId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/public/product-reviews/${subdomain}/${productId}`);
      setReviews(r.data.reviews || []);
      setTotal(r.data.total || 0);
      setAverage(r.data.average || 0);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [subdomain, productId]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.body || form.body.trim().length < 5) {
      toast.error('Please write at least a few words');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/product-reviews/${subdomain}/${productId}`, form);
      toast.success('Review submitted — thank you!');
      setShowForm(false);
      setForm({ rating: 5, title: '', body: '', author_name: '', buyer_email: buyerEmail });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-8 pt-8 border-t border-white/10" data-testid="product-reviews-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-white font-bold text-xl">Reviews</h3>
          {total > 0 ? (
            <div className="mt-1.5 flex items-center gap-2">
              <Stars rating={Math.round(average)} size={16} />
              <span className="text-white font-bold">{average.toFixed(1)}</span>
              <span className="text-white/50 text-sm">({total} review{total !== 1 ? 's' : ''})</span>
            </div>
          ) : (
            <p className="text-white/50 text-sm mt-1">No reviews yet — be the first!</p>
          )}
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-xs font-bold uppercase tracking-wider"
          data-testid="write-review-btn"
        >
          {showForm ? 'Cancel' : 'Write a review'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mt-5 p-5 rounded-xl border border-white/10 bg-white/[0.03] space-y-3" data-testid="review-form">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-white/60">Your rating</label>
            <div className="mt-1.5"><Stars rating={form.rating} size={22} onSelect={(v) => setForm({ ...form, rating: v })} /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              value={form.author_name}
              onChange={(e) => setForm({ ...form, author_name: e.target.value })}
              placeholder="Your name (optional)"
              className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:border-[#A78BFA]/50 outline-none"
              data-testid="review-author"
            />
            <input
              value={form.buyer_email}
              onChange={(e) => setForm({ ...form, buyer_email: e.target.value })}
              placeholder="Email (for verified badge)"
              type="email"
              className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:border-[#A78BFA]/50 outline-none"
              data-testid="review-email"
            />
          </div>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Headline (optional)"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:border-[#A78BFA]/50 outline-none"
            data-testid="review-title"
          />
          <textarea
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            rows={3}
            placeholder="Tell others what you loved (or didn't)"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:border-[#A78BFA]/50 outline-none resize-none"
            data-testid="review-body"
          />
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50"
            data-testid="review-submit"
          >
            {submitting ? <Loader2 size={12} className="animate-spin" /> : null}
            Submit review
          </button>
        </form>
      )}

      {loading && <div className="mt-6 text-white/40 text-sm flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>}

      {!loading && reviews.length > 0 && (
        <div className="mt-6 space-y-4" data-testid="review-list">
          {reviews.map((r) => (
            <div key={r.review_id} className="p-4 rounded-xl border border-white/8 bg-white/[0.02]" data-testid={`review-item-${r.review_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Stars rating={r.rating} size={13} />
                  <span className="text-white font-bold text-sm">{r.author_name || 'Anonymous'}</span>
                  {r.verified_purchase && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[9px] font-bold border border-emerald-400/25">
                      <ShieldCheck size={9} /> Verified
                    </span>
                  )}
                </div>
                <span className="text-white/40 text-[10px]">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              {r.title && <p className="mt-2 text-white/90 font-bold text-sm">{r.title}</p>}
              <p className="mt-1 text-white/70 text-sm whitespace-pre-wrap">{r.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
